import type { AgentAction, AgentActionStats, ReportFocus, StatusFilter } from './types';

export function isFeedErrorRow(action: Pick<AgentAction, 'status' | 'success' | 'subtitle'>): boolean {
  const status = String(action.status || '');
  if (/Caller error:/i.test(action.subtitle || '')) return true;
  if (status === 'caller_error') return true;
  if (status === 'resolved') return false;
  return action.success === false || status === 'failed' || status === 'error' || status === 'fix_failed';
}

function resolveToolName(action: Pick<AgentAction, 'tool_name' | 'subtitle' | 'title'>): string {
  if (action.tool_name) return action.tool_name.toLowerCase();
  const via = action.subtitle?.match(/via (dc__[a-z0-9_]+)/i);
  if (via?.[1]) return via[1].toLowerCase();
  const called = action.title?.match(/called (dc__[a-z0-9_]+)/i);
  return called?.[1]?.toLowerCase() || '';
}

export function matchesStatusFilter(action: AgentAction, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  const tool = resolveToolName(action);
  const status = action.status || '';

  switch (filter) {
    case 'failures':
      return isFeedErrorRow(action);
    case 'fixes':
      return action.type === 'fix';
    case 'deploys':
      return action.type === 'deploy' || action.type === 'promote'
        || tool.includes('deploy') || tool.includes('promote');
    case 'pending':
      return status === 'pending_confirmation' || status === 'pending';
    case 'agent_calls':
      return tool.includes('create_agent') || tool.includes('execute_agent');
    case 'data_sync':
      return tool.includes('pim') || tool.includes('entity') || tool.includes('dam')
        || tool.includes('iot') || tool.includes('device');
    case 'code_commits':
      return tool.includes('repo_push') || tool.includes('repo_pull');
    default:
      return true;
  }
}

export function filterActionsByStatus(actions: AgentAction[], filter: StatusFilter): AgentAction[] {
  if (filter === 'all') return actions;
  return actions.filter((a) => matchesStatusFilter(a, filter));
}

export function computeFilteredStats(actions: AgentAction[]): AgentActionStats {
  let fixes = 0;
  let deploys = 0;
  let failed = 0;
  let agentRuns = 0;

  for (const a of actions) {
    if (a.type === 'fix') {
      agentRuns += 1;
      if (a.status === 'error' || a.status === 'fix_failed' || a.status === 'failed') failed += 1;
      else if (a.status !== 'no_fix') fixes += 1;
    } else if (a.type === 'deploy' || a.type === 'promote') {
      deploys += 1;
      agentRuns += 1;
    } else {
      agentRuns += 1;
      if (isFeedErrorRow(a)) failed += 1;
    }
  }

  return { agent_runs: agentRuns, fixes, deploys, failed };
}

export function statusFilterToReportParams(filter: StatusFilter): {
  focus: ReportFocus;
  status_filter?: StatusFilter;
} {
  switch (filter) {
    case 'failures':
      return { focus: 'failures' };
    case 'fixes':
      return { focus: 'fixes' };
    case 'deploys':
      return { focus: 'deploys' };
    case 'pending':
      return { focus: 'pending' };
    case 'agent_calls':
    case 'data_sync':
    case 'code_commits':
      return { focus: 'all', status_filter: filter };
    default:
      return { focus: 'all' };
  }
}
